import { useEffect, useState } from 'react';
import type { VisitorConfig } from '../../types';
import FloatingChatButton from './FloatingChatButton';
import { CookieConsentBanner } from './CookieConsentBanner';

// Regroupe les éléments flottants communs à toutes les pages du site public
// (bouton de chat, bandeau cookies) : les deux se disputeraient le bas de
// l'écran montés indépendamment, ce composant décale le bouton de chat
// au-dessus du bandeau tant que celui-ci est affiché.

const STORAGE_KEY = 'feedit-cookie-notice-dismissed';
// Même clé que server/googleTag.ts (CONSENT_STORAGE_KEY) : /tracking.js la lit
// au chargement pour restaurer l'accord d'un visiteur déjà venu.
const CONSENT_KEY = 'feedit-cookie-consent';
// Émis par le lien « Gérer les cookies » du pied de page pour rouvrir le choix.
export const REOPEN_COOKIE_CHOICE_EVENT = 'feedit-reopen-cookie-choice';

// Hauteur approximative du bandeau (une ligne de texte + marges) : suffisante
// pour ne pas superposer le bouton, sans avoir à mesurer le DOM.
const BANNER_LIFT_PX = 64;

type ConsentChoice = 'granted' | 'denied';

interface SiteWidgetsProps {
  config: VisitorConfig;
}

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string | null) {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // Navigation privée ou stockage bloqué : le bandeau réapparaîtra à la
    // prochaine visite, ce n'est pas plus grave qu'avant ce réglage.
  }
}

function updateGoogleConsent(choice: ConsentChoice) {
  const gtag = (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag;
  gtag?.('consent', 'update', {
    ad_storage: choice,
    ad_user_data: choice,
    ad_personalization: choice,
    analytics_storage: choice,
  });
}

export function SiteWidgets({ config }: SiteWidgetsProps) {
  const [dismissed, setDismissed] = useState(true);
  const [choice, setChoice] = useState<ConsentChoice | null>('denied');
  const tracking = Boolean(config.googleTagId?.trim());

  useEffect(() => {
    // Sans stockage lisible, readStorage renvoie null : le bandeau s'affiche
    // à chaque visite plutôt que de faire échouer le rendu de la page.
    setDismissed(readStorage(STORAGE_KEY) === '1');
    const stored = readStorage(CONSENT_KEY);
    setChoice(stored === 'granted' || stored === 'denied' ? stored : null);

    const reopen = () => {
      writeStorage(CONSENT_KEY, null);
      updateGoogleConsent('denied');
      setChoice(null);
    };
    window.addEventListener(REOPEN_COOKIE_CHOICE_EVENT, reopen);
    return () => window.removeEventListener(REOPEN_COOKIE_CHOICE_EVENT, reopen);
  }, []);

  // Avec une balise publicitaire, le bandeau est obligatoire : il ne dépend
  // plus du réglage « Afficher le bandeau » de l'agence.
  const bannerVisible = tracking
    ? choice === null
    : config.cookieBannerEnabled !== false && !dismissed && Boolean(config.cookieBannerText?.trim());

  const dismiss = () => {
    setDismissed(true);
    writeStorage(STORAGE_KEY, '1');
  };

  const decide = (next: ConsentChoice) => {
    setChoice(next);
    writeStorage(CONSENT_KEY, next);
    updateGoogleConsent(next);
  };

  return (
    <>
      <FloatingChatButton config={config} liftPx={bannerVisible ? BANNER_LIFT_PX : 0} />
      {bannerVisible && (
        <CookieConsentBanner
          config={config}
          onDismiss={dismiss}
          onAccept={() => decide('granted')}
          onRefuse={() => decide('denied')}
        />
      )}
    </>
  );
}
